import React, { useState } from "react";
import { SparklesIcon } from "@heroicons/react/24/outline";
import { toast } from "sonner";
import type { ReferenceType } from "../types/reference";
import { validateFiles } from "../utils/fileValidation";

interface MediaUploadSectionProps {
  type: ReferenceType;
  imageFiles: File[];
  setImageFiles: (files: File[]) => void;
  existingImageUrl?: string;
  extractedThumbnail?: string | null;
  onAutoFillWithAI?: () => void;
  canUseAI?: boolean;
  analysisState?: 'idle' | 'analyzing' | 'complete' | 'error';
}

export function MediaUploadSection({
  type,
  imageFiles,
  setImageFiles,
  existingImageUrl,
  extractedThumbnail,
  onAutoFillWithAI,
  canUseAI = false,
  analysisState = 'idle'
}: MediaUploadSectionProps) {
  const [dragOverForm, setDragOverForm] = useState(false);

  // Debug logging
  console.log('MediaUploadSection render - imageFiles:', imageFiles, 'length:', imageFiles.length);
  imageFiles.forEach((file, index) => {
    console.log(`File ${index}:`, {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified
    });
  });

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      
      // Validate files before adding them
      const { validFiles, errors } = validateFiles(newFiles, type);
      
      if (errors.length > 0) {
        toast.error(`Some files were rejected: ${errors.join(', ')}`);
      }
      
      if (validFiles.length > 0) {
        setImageFiles([...imageFiles, ...validFiles]);
        toast.success(`${validFiles.length} file(s) added!`);
      }
    }
  };

  const handleRemoveImage = (index: number) => {
    setImageFiles(imageFiles.filter((_, i) => i !== index));
  };

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOverForm(false);

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    const { validFiles, errors } = validateFiles(fileArray, type);
    
    if (errors.length > 0) {
      toast.error(`Some files were rejected: ${errors.join(', ')}`);
    }

    if (validFiles.length > 0) {
      setImageFiles([...imageFiles, ...validFiles]);
      
      // Auto-detect type based on files
      const hasVideo = validFiles.some(file => file.type.startsWith('video/'));
      if (hasVideo && type === "screenshot") {
        toast.success(`${validFiles.length} file(s) added! Consider changing type to "flow" for video support.`);
      } else {
        toast.success(`${validFiles.length} file(s) added!`);
      }
    } else if (fileArray.length > 0) {
      toast.error("No valid files to add. Please drop image or video files.");
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.types.includes('Files')) {
      setDragOverForm(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverForm(false);
    }
  };

  const handlePasteImage = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.indexOf("image") !== -1) {
        const file = item.getAsFile();
        if (file) {
          setImageFiles([...imageFiles, file]);
          toast.success("Screenshot pasted!");
          e.preventDefault();
          break;
        }
      }
    }
  };

  const isRequired = (type === "screenshot" || type === "component" || type === "flow") && !existingImageUrl && !extractedThumbnail;

  return (
    <div 
      onPaste={handlePasteImage} 
      onDrop={handleFileDrop}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      className={`relative border-2 border-dashed rounded-lg p-4 transition-colors ${
        dragOverForm 
          ? 'border-primary bg-primary/5' 
          : 'border-gray-300 hover:border-gray-400'
      }`}
    >
      <label htmlFor="imageFile" className="block text-sm font-medium text-gray-700">
        Media File(s) {isRequired && <span className="text-red-500">*</span>}
        <span className="text-xs text-gray-500 ml-2">
          (Upload images{type === "flow" ? ", videos, or other media files" : " or media files"})
        </span>
      </label>
      
      {/* AI Auto-Fill Button */}
      {canUseAI && imageFiles.length > 0 && analysisState === 'idle' && onAutoFillWithAI && (
        <div className="mb-3">
          <button
            type="button"
            onClick={onAutoFillWithAI}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-medium rounded-md hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-sm"
          >
            <SparklesIcon className="w-4 h-4" />
            Auto-Fill with AI
          </button>
          <p className="text-xs text-gray-500 mt-1">
            Let AI analyze your media and suggest form values
          </p>
        </div>
      )}

      <input
        type="file"
        id="imageFile"
        accept={type === "flow" ? "image/*,video/*" : "image/*"}
        multiple
        onChange={handleImageChange}
        className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary-light file:text-primary hover:file:bg-primary-hover file:cursor-pointer"
      />
      <p className="text-xs text-gray-500 mt-1">
        You can also paste screenshots (Ctrl+V) or drag & drop files here
      </p>
      
      {/* Current image preview for existing references */}
      {existingImageUrl && imageFiles.length === 0 && (
        <div className="mt-2">
          <p className="text-xs text-gray-500">Current image:</p>
          <img src={existingImageUrl} alt="Current" className="max-h-32 rounded mt-1 border"/>
        </div>
      )}
      
      {/* Extracted thumbnail preview */}
      {extractedThumbnail && imageFiles.length === 0 && !existingImageUrl && (
        <div className="mt-2">
          <p className="text-xs text-gray-500">Extracted thumbnail from URL:</p>
          <img src={extractedThumbnail} alt="Extracted thumbnail" className="max-h-32 rounded mt-1 border"/>
        </div>
      )}
      
      {/* File previews */}
      {imageFiles.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {imageFiles.map((file, idx) => (
            <div key={`${file.name}-${file.size}-${idx}`} className="relative inline-block">
              {file.type.startsWith('image/') ? (
                <img 
                  src={URL.createObjectURL(file)} 
                  alt={`Preview ${idx+1}`} 
                  className="max-h-32 rounded border"
                />
              ) : file.type.startsWith('video/') ? (
                <div className="relative">
                  <video 
                    src={URL.createObjectURL(file)} 
                    className="max-h-32 rounded border"
                    controls
                    preload="metadata"
                  >
                    <track kind="captions" />
                  </video>
                  <div className="absolute top-1 left-1 bg-black bg-opacity-75 text-white text-xs px-1 rounded">
                    VIDEO
                  </div>
                </div>
              ) : (
                <div className="max-h-32 w-32 rounded border bg-gray-100 flex items-center justify-center">
                  <div className="text-center p-2">
                    <div className="text-xs text-gray-600">{file.name}</div>
                    <div className="text-xs text-gray-400">{(file.size / 1024 / 1024).toFixed(1)}MB</div>
                  </div>
                </div>
              )}
              <button 
                type="button" 
                onClick={() => handleRemoveImage(idx)} 
                className="absolute top-0 right-0 bg-white bg-opacity-80 rounded-full p-1 text-xs text-red-600 hover:bg-opacity-100"
              >
                &times;
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
} 